<script setup>
    import { ref, onMounted, defineAsyncComponent } from 'vue';
    import router from '@/router';
    import store from '@/store';
    import { config } from '@/config.js';
    import { useAmigoAbonadoSga } from '@/composables/front/checkout/useAmigoAbonadoSga';
    import { useSgaCheckoutSteps } from '@/composables/front/checkout/useSgaCheckoutSteps';
    import { useEventoTracking } from '@/composables/front/analytics/useEventoTracking';

    // Componentes — Stepper e iconos cargados static (crítico para el UX)
    import Stepper from '@/components/frontend/events/compra/components/Stepper.vue';
    import CuentaAtras from '@/components/frontend/events/compra/CuentaAtras';

    // Componentes de pasos — lazy loading para reducir bundle inicial
    const SeleccionarPerfilSga = defineAsyncComponent(() => import('./SeleccionarPerfilSga.vue'));
    const SeleccionarEntitlementSga = defineAsyncComponent(() => import('./SeleccionarEntitlementSga.vue'));
    const SeleccionarZonaComponent = defineAsyncComponent(() => import('@/components/frontend/abonos/sga/SeleccionarZonaComponent.vue'));
    const SeleccionarAsientosSga = defineAsyncComponent(() => import('@/components/frontend/abonos/sga/SeleccionarAsientosSga.vue'));
    const DatosCompradoresSgaComponent = defineAsyncComponent(() => import('@/components/frontend/events/compra/sga/DatosCompradoresSgaComponent.vue'));
    const ResumenCompraComponent = defineAsyncComponent(() => import('@/components/frontend/components/sga/ResumenCompraComponent.vue'));
    const ProcesarPago = defineAsyncComponent(() => import('../../../abonos/sga/ProcesarPago.vue'));

    const props = defineProps({
        event: {
            type: Object,
            required: true
        }
    });

    const stepper = ref(null);
    
    // ── COMPOSABLE: Lógica de autenticación y perfiles SGA ──────────────────
    const { 
        loading, 
        profiles, 
        entitlements, 
        error, 
        isAuthenticated,
        fetchProfiles, 
        selectProfile, 
        selectedProfile,
        selectEntitlement,
        selectedEntitlementId,
        selectedProfileId,
        buyerId
    } = useAmigoAbonadoSga(props.event.id);

    // ── COMPOSABLE: Estado de checkout (pasos, carrito, datos) ──────────────
    const {
        steps,
        zonaSeleccionada,
        itemsCarrito,
        sessionId,
        formulario,
        jetId,
        cartId,
        datosComprador,
        setZonaSeleccionada,
        clearZonaSeleccionada,
        onSesionCreada,
        setDatosCobro,
        updateDatosCompradores,
        eliminarDatos
    } = useSgaCheckoutSteps();

    // Handlers
    function handleProfileSelect(profile, changeStep) {
        selectProfile(profile);
        changeStep();
    }

    function handleEntitlementSelect(id, changeStep) {
        selectEntitlement(id);
        changeStep();
    }

    /**
     * Maneja el evento loginRequired emitido por SeleccionarPerfilSga
     * Redirige al usuario a la página de login con un parámetro de redirección opcional
     */
    function handleLoginRequired() {
        // Obtener la URL actual para redirigir después del login
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;
        
        // Navegar a la ruta de login con el parámetro de redirección
        router.push({
            name: 'login',
            query: { redirect: currentUrl !== '/' ? currentUrl : undefined }
        });
    }

    function trackSesionCreada(items, carritoId) {
        onSesionCreada(items, carritoId);

        const normalizedItems = Array.isArray(items) ? items : (itemsCarrito.value || []);

        const { trackBeginCheckout } = useEventoTracking({ store, getVuexAlias: config.getVuexAlias });
        trackBeginCheckout({
            evento_id: props.event?.id,
            evento_name: props.event?.nombre || props.event?.name,
            entradas: (normalizedItems || []).map(item => ({
                price: parseFloat(item.precio?.amount || item.price || 0),
                unidades: item.unidades || 1
            })),
            productos: [],
            total_price: (normalizedItems || []).reduce((sum, item) => sum + ((parseFloat(item.precio?.amount || item.price || 0)) * (item.unidades || 1)), 0),
            moneda: 'EUR'
        });
    }

    onMounted(() => {
        fetchProfiles();
        eliminarDatos();
    });
</script>

<template>
    <div class="proceso-compra-amigo-abonado-sga">
        <CuentaAtras @tiempoTerminado="() => stepper.changeStep(false, true)"/>
        
        <section class="padding-vertical-content">
            <Stepper ref="stepper" :steps="steps">
                <!-- Paso 1: Selección de Perfil -->
                <template #pasoPerfil="{ changeStep }">
                    <SeleccionarPerfilSga 
                        :profiles="profiles" 
                        :loading="loading" 
                        :error="error"
                        :is-authenticated="isAuthenticated"
                        :selected-profile-id="selectedProfileId"
                        @select="(p) => handleProfileSelect(p, changeStep)"
                        @login-required="handleLoginRequired"
                    />
                </template>

                <!-- Paso 2: Selección de Entitlement -->
                <template #pasoEntitlement="{ changeStep }">
                    <SeleccionarEntitlementSga 
                        :entitlements="Array.isArray(entitlements) ? entitlements : entitlements.data || []" 
                        :loading="loading" 
                        :error="error"
                        :selected-entitlement-id="selectedEntitlementId"
                        @select="(ent) => handleEntitlementSelect(ent.id, changeStep)"
                    />
                </template>

                <!-- Paso 3: Selección de Zona -->
                <template #pasoSector="{ changeStep }">
                    <SeleccionarZonaComponent 
                        :sga_event_id="props.event.sga_id"
                        :is-private="true"
                        @zonaSeleccionada="(zona) => {
                            setZonaSeleccionada(zona);
                            changeStep();
                        }" 
                    />
                </template>

                <!-- Paso 4: Selección de Asientos -->
                <template #pasoAsiento="{ changeStep }">
                    <SeleccionarAsientosSga 
                        tipo="eventos"
                        :zona="zonaSeleccionada" 
                        :sga_event_id="props.event.sga_id"
                        :event_session_id="props.event.sesion_id"
                        :is-private="true"
                        :buyer-id="buyerId"
                        :licensor-entitlement-id="selectedEntitlementId"
                        @sesionCreada="(items, carritoId) => {
                            trackSesionCreada(items, carritoId);
                            stepper.changeStep();
                        }" 
                        @volver="() => {
                            clearZonaSeleccionada();
                            changeStep(true);
                        }" 
                    />
                </template>

                <!-- Paso 5: Datos Compradores -->
                <template #pasoDatos="{ changeStep }">
                    <DatosCompradoresSgaComponent
                        :items_carrito="itemsCarrito"
                        :sga_event_id="props.event.sga_id"
                        :datos_comprador="datosComprador"
                        :selected-profile="selectedProfile"
                        @fillDatosCompradores="updateDatosCompradores"
                        @setDatosCobro="(j, f, s, c) => {
                            setDatosCobro(j, f, s, c);
                            changeStep();
                        }"
                        @volver="() => {
                            changeStep(true);
                        }"
                    />
                </template>

                <!-- Paso 6: Resumen -->
                <template #pasoResumen="{ changeStep }">
                    <ResumenCompraComponent
                        :items="itemsCarrito"
                        :event="props.event"
                        ga4-category="Entrada"
                        :jet_id="jetId"
                        :formulario="formulario"
                        :cart_id="cartId"
                        @setDatosCobro="setDatosCobro"
                        @terminado="() => changeStep()"
                        @volver="() => changeStep(true)"
                    />
                </template>

                <!-- Paso 7: Pago (Opcional) -->
                <template #pasoTarjeta="{ changeStep }">
                    <ProcesarPago 
                        :jet_id="jetId"
                        :session_id="sessionId"
                        @terminado="() => changeStep()"
                    />
                </template>
            </Stepper>
        </section>
    </div>
</template>
